import React, { useCallback, useState } from "react";
import { connect } from "react-redux";
import { CardNumberElement } from "@stripe/react-stripe-js";
import _ from "lodash";

import DashboardView from "views/Administration/BuyCredit";
import PaymentService from "api/Payment";
import AccountService from "api/Account";
import UserService from "api/User";
import DirectDialService from "api/DirectDial";
import DirectAreaService from "api/DirectArea";
import SettingsActions from "store/reducers/Settings";
import AccountActions from "store/reducers/Account";
import UserActions from "store/reducers/User";
import { handlePromise, getCountryName } from "utils";
import { useFetch } from "hooks";

const Dashboard = ({
  parentPath,
  account,
  userEmail,
  locale,
  updateAccountInfo,
  updateUser,
  toggleLoading,
  user,
  toggleErrorAlert,
}) => {
  const [accountLoading, setAccountLoading] = useState(false);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [phoneNumbersLoading, setPhoneNumbersLoading] = useState(false);

  const [accountData] = useFetch({
    initialState: {},
    promise: AccountService.getMyAccount,
    toggleErrorAlert,
  });

  const fetchPhoneNumbers = useCallback(async (id) => {
    setPhoneNumbersLoading(true);
    const country = _.split(id, "-")[0] || null;
    const area = _.split(id, "-")[1] || null;
    const filters = {
      limit: 0,
      "filters[country]": country,
      panel:'user'
    };
    if (area) filters["filters[area]"] = area;

    const [, response, responseData] = await handlePromise(
      DirectDialService.getDdis(filters)
    );
    setPhoneNumbersLoading(false);
    if (response.ok) setPhoneNumbers(responseData.data);
  }, []);

  const [countries, countriesLoading] = useFetch({
    initialState: [],
    promise: () => DirectAreaService.getCountries({ limit: 0 }),
    format: (dataToFormat) =>
      dataToFormat
        ? _.chain(dataToFormat)
            .map((item) => {
              const country = getCountryName(item.country, locale);
              return {
                id: item.area ? `${item.country}-${item.area}` : item.country,
                name: item.area ? `${country} - ${item.area}` : country,
              };
            })
            .value()
        : [],
  });

  const reloadAccount = useCallback(async () => {
    setAccountLoading(true);
    const [, accountResponse, accountResponseData] = await handlePromise(
      AccountService.getMyAccount()
    );
    if (!accountResponse.ok) return setAccountLoading(false);
    updateAccountInfo(accountResponseData);

    const [, response, responseData] = await handlePromise(
      UserService.getUser()
    );
    setAccountLoading(false);
    if (response.ok) {
      updateUser(responseData);
    }
  }, [updateAccountInfo, updateUser]);

  const submitForm = useCallback(
    async (paymentData, stripe, elements, callback) => {
      if (paymentData.total) {
        toggleLoading(true);

        const result = await stripe.createPaymentMethod({
          type: "card",
          card: elements.getElement(CardNumberElement),
          billing_details: {
            email: userEmail,
            name: account.fiscalName || account.name,
            address: {
              city: account.city,
              country: account.country,
              postal_code: account.postalCode,
              line1: account.address,
            },
          },
        });

        if (!result) return "apiError";
        if (result.error) {
          if (result.error.message) return result.error.message;
          return "apiError";
        }

        const [errors, response, responseData] = await handlePromise(
          PaymentService.buyDdi({
            email: userEmail,
            paymentMethod: result.paymentMethod.id,
            ddi: paymentData.ddi,
            type: "ddi",
            // withTax: ((accountData && accountData.taxes) || (accountData && accountData.vat=="")) ? 1 : 0,
          })
        );
        if (!response.ok) {
          toggleLoading(false);
          return errors;
        }

        if (responseData.status === "requires_action") {
          const confirmResult = await stripe.confirmCardPayment(
            responseData.client_secret
          );

          if (confirmResult.error) {
            if (!confirmResult.error.payment_intent) {
              toggleLoading(false);
              return confirmResult.error.message || "apiError";
            }
            confirmResult.error.payment_intent['email'] = userEmail;
            const [updateErrors, updateResponse] = await handlePromise(
              PaymentService.updateDdiSubscription(responseData.id, {
                data: JSON.stringify(confirmResult.error.payment_intent),
              })
            );
            toggleLoading(false);
            if (confirmResult.error.message) return confirmResult.error.message;
            if (!updateResponse.ok) return updateErrors;
            return "apiError";
          }
          confirmResult.paymentIntent['email'] = userEmail;
          const [updateErrors, updateResponse] = await handlePromise(
            PaymentService.updateDdiSubscription(responseData.id, {
              data: JSON.stringify(confirmResult.paymentIntent),
            })
          );
          toggleLoading(false);
          if (!updateResponse.ok) return updateErrors;
          return callback();
        }
        toggleLoading(false);
        return callback();
      }
    },
    // [account, userEmail, toggleLoading,accountData]
    [account, userEmail, toggleLoading]
  );

  return (
    <DashboardView
      accountData={accountData}
      phoneNumbers={phoneNumbers}
      countries={countries}
      countriesLoading={countriesLoading}
      isLoading={accountLoading || phoneNumbersLoading}
      parentPath={parentPath}
      onFetchPhoneNumbers={fetchPhoneNumbers}
      onSubmit={submitForm}
      onReloadAccount={() => {
        setPhoneNumbers([]);
        reloadAccount();
      }}
      userData={user}
    />
  );
};

const mapStateToProps = ({ account, user, settings }) => ({
  account,
  locale: _.get(settings, "locale.locale"),
  userEmail: _.get(user, "email"),
  user,
});

const mapDispatchToProps = {
  toggleErrorAlert: SettingsActions.toggleErrorAlert,
  toggleLoading: SettingsActions.toggleLoading,
  updateAccountInfo: AccountActions.updateInfo,
  updateUser: UserActions.updateUser,
};

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard);
